/* eslint-disable arrow-body-style */
/* eslint-disable @typescript-eslint/no-misused-promises */

import React, { useImperativeHandle } from 'react';
import classnames from 'classnames';
import { omit } from 'lodash-es';
import { TableColumn } from 'tea-component';
import ReportTable from '../../../components/ReportTable';
import useMapping from '@gp/hooks/useMapping';
import useDPSReportModule from '../../../_hooks/useDPSReportModule';
import { getMemoMaterials } from '../../services';
import { getRecordKeyOfMockable } from '@gp/utils/common';
import { resetEditorContent } from '../../../utils';
import { getIsShowGRDate } from '../../utils';
import { CARD_TITLE, buildRecord, getColumnsEditable } from './utils';

import type { IBaseProps } from '@home/types/common';
import type { IRecord } from '@gp/types/global';
import type { IDPSReportFilter, IDPSReportCalendarProjectSimpleVO } from '@gp/types/dpsReport/common';
import type { IDPSReportMemoPendingApprovalVO } from '@gp/types/dpsReport/v3';
import type { IDPSReportModuleInstance } from '../../types';

export interface IMemoPendingApprovalInstance extends IDPSReportModuleInstance {}

interface IMemoPendingApprovalProps extends IBaseProps {
  // 标题
  title?: string;
  onTitleChange?: (payload: string) => void;
  // 是否loading
  isLoading?: boolean;
  filter: IDPSReportFilter;
  // projects集合数据
  projects?: IDPSReportCalendarProjectSimpleVO[] | null;
  // 模块数据
  value: IDPSReportMemoPendingApprovalVO[];
  // 模块数据更新，删除后立马更新，新增后 需要等新增这条数据编辑完整后 才会更新，页面就不需要考虑不完整的脏数据了
  onChange?: (payload: IDPSReportMemoPendingApprovalVO[]) => void;
}

const MemoPendingApproval: React.ForwardRefRenderFunction<IMemoPendingApprovalInstance, IMemoPendingApprovalProps> = (
  props,
  ref,
) => {
  const { style, className, title = CARD_TITLE, onTitleChange, isLoading, filter, projects, value, onChange } = props;

  const { start_date: startDate } = filter || {};

  const showGRDate = getIsShowGRDate(startDate);

  const { records, setRecords, onAdd, onDelete } = useDPSReportModule<IDPSReportMemoPendingApprovalVO>({
    buildRecord,
    value,
    onChange,
  });

  const [bubbleVisiblesMapping, { set: setBubbleVisiblesMapping, setAll: setAllBubbleVisiblesMapping }] = useMapping<
  boolean | undefined
  >({});

  const columns: TableColumn<IDPSReportMemoPendingApprovalVO>[] = getColumnsEditable({
    showGRDate,
    projects: projects || [],
    bubbleVisiblesMapping,
    onProjectChange: async (cvm: IDPSReportMemoPendingApprovalVO, payload: string) => {
      try {
        const { mock_id: mockId } = cvm || {};
        const recordIdx = records?.findIndex((record) => record?.mock_id === mockId);
        if (recordIdx !== -1) {
          const rootNode = document.querySelector('.dbg-dps-report-memo-pending-approval-table');
          resetEditorContent(rootNode, recordIdx);

          const project = projects?.find((item) => {
            const { project_id: projectId } = item || {};
            return projectId === payload;
          });
          let recordTmp = {
            ...records?.[recordIdx],
            ...omit(project, 'business_owner', 'pteam'),
            comment: '',
          };
          let recordsTmp = [...records?.slice(0, recordIdx), recordTmp, ...records?.slice(recordIdx + 1)];
          setRecords(recordsTmp);
          onChange?.(recordsTmp);
          const {
            milestone_id: milestoneId,
            material: materials,
            date,
            date_confirm: dateConfirm,
          } = await getMemoMaterials({
            ...filter,
            project_id: payload,
          });
          recordTmp = {
            ...recordTmp,
            milestone_id: milestoneId,
            memo_open: !!milestoneId && Array.isArray(materials) && materials.length > 0,
            material: materials || [],
            date,
            date_confirm: dateConfirm,
          };
          recordsTmp = [...records?.slice(0, recordIdx), recordTmp, ...records?.slice(recordIdx + 1)];

          if (!Array.isArray(materials) || !materials.length) {
            setBubbleVisiblesMapping(payload, true);
            setTimeout(() => {
              setBubbleVisiblesMapping(payload, undefined);
            }, 3000);
          } else {
            setBubbleVisiblesMapping(payload, false);
          }

          setRecords(recordsTmp);
          onChange?.(recordsTmp);
        }
      } catch (exception) {}
    },
    onCommentChange: (cvm: IDPSReportMemoPendingApprovalVO, payload: string) => {
      const { mock_id: mockId } = cvm || {};
      const recordIdx = records.findIndex((item) => item?.mock_id === mockId);
      const recordTmp = {
        ...records[recordIdx],
        comment: payload || '',
      };
      const recordsTmp = [...records.slice(0, recordIdx), recordTmp, ...records.slice(recordIdx + 1)];
      setRecords(recordsTmp);
      onChange?.(recordsTmp);
    },
    onMemoChange: (cvm: IDPSReportMemoPendingApprovalVO, payload: boolean) => {
      const { mock_id: mockId } = cvm || {};
      const recordIdx = records.findIndex((item) => item?.mock_id === mockId);
      const record = records[recordIdx];
      const recordTmp = { ...record, memo_open: payload };
      const recordsTmp = [...records.slice(0, recordIdx), recordTmp, ...records.slice(recordIdx + 1)];
      setRecords(recordsTmp);
      onChange?.(recordsTmp);
    },
  });

  useImperativeHandle(ref, () => ({
    initialize: (payload) => {
      const { memo } = payload || {};
      const initialAllBubbleVisiblesMapping = memo?.reduce<IRecord<boolean | undefined>>((acc, cur) => {
        const { project_id: projectId, milestone_id: milestoneId, material: materials } = cur || {};
        const hasMaterials = !!milestoneId && Array.isArray(materials) && materials.length > 0;
        return { ...acc, [projectId]: hasMaterials ? false : undefined };
      }, {});
      setAllBubbleVisiblesMapping(initialAllBubbleVisiblesMapping || {});
    },
  }));

  return (
    <ReportTable
      style={style}
      className={classnames(
        'dbg-table-vertical-align-top dbg-dps-report__hightlights-table-card dbg-dps-report-memo-pending-approval-table',
        className,
      )}
      isSupportRenameTitle
      // title={title}
      title={title || CARD_TITLE}
      onTitleChange={onTitleChange}
      isLoading={isLoading}
      columns={columns}
      records={records}
      recordKey={getRecordKeyOfMockable}
      onAdd={onAdd}
      onDelete={onDelete}
    />
  );
};

// @ts-ignore
export default React.forwardRef(MemoPendingApproval);
